import { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { handleFilterChange } from "../../helpers";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import useMutation from "../../hooks/useMutation";
import SubscriptionModal from "../../components/sections/admin/subscription/SubscriptionModal";
import dayjs from "dayjs";
import { useGetPermission } from "../../hooks/useGetPermission";
import {
  transactionList,
  transactionStatusChange,
} from "../../services/transactionService";
import { useNavigate } from "react-router-dom";
import { pdf } from "@react-pdf/renderer"; // your PDF document
import fileSaver from "file-saver"; // to trigger download
import DonationReciptPdf from "../../pdfs/DonationReciptPdf";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import { subsciptionStatusChange } from "../../services/subscriptionService";

const Subscription = () => {
  setTitle("Vpeters Admin | Recurring Donations");
  const { permission, status } = useGetPermission("recurringDonations");

  const [selectedItems, setSelectedItems] = useState([]);
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["recurringDonations", filters, search, permission?.list],
    fetchFunction: async () => {
      const res = await transactionList({
        ...filters,
        categoryType: "donation",
        isRecurring: true,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!permission?.list && status === "ok",
  });

  //   Finance status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await subsciptionStatusChange({
          subscriptionId: id,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status === 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) =>
              elm?.subscription?._id === payload?.id
                ? {
                    ...elm,
                    subscription: {
                      ...elm?.subscription,
                      status: payload?.status === 1 ? 4 : 1,
                    },
                  }
                : elm
            );
            pre.metadata = {
              ...pre.metadata,
              extraCount: {
                ...pre.metadata.extraCount,
                cancelSubscription:
                  payload?.status === 1
                    ? pre.metadata.extraCount.cancelSubscription + 1
                    : pre.metadata.extraCount.cancelSubscription - 1,
              },
            };
            return { ...pre };
          });
          setDeleteModal((pre) => ({ data: null, isOpen: false }));
        } else {
          toast.warning(res?.message || res?.error);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleDownloadReceipt = async (item) => {
    const fileName = `Receipt-${item?.invoiceId || "N/A"}.pdf`;
    const blob = await pdf(<DonationReciptPdf data={item} />).toBlob();
    fileSaver.saveAs(blob, fileName);
  };

  const tableHeadData = [
    {
      _id: 2,
      name: "Donor Name",
      align: "left",
    },
    {
      _id: 3,
      name: "Email",
      align: "left",
    },
    {
      _id: 4,
      name: "Amount",
      align: "left",
    },
    {
      _id: 5,
      name: "Category",
      align: "left",
    },
    {
      _id: 6,
      name: "Type",
      align: "left",
    },
    {
      _id: 7,
      name: "Organization Name",
      align: "left",
    },
    {
      _id: 8,
      name: "Reference#",
      align: "left",
    },
    {
      _id: 9,
      name: "Date",
      align: "left",
    },
    {
      _id: 10,
      name: "Payment Method",
      align: "left",
    },
    {
      _id: 11,
      name: "Status",
      align: "left",
    },
    {
      _id: 12,
      name: "Auto Email Sent",
      align: "left",
    },
    {
      _id: 13,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.email || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.amount || "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.donationFrequency || "-",
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.donationType || "-",
        },
        {
          _id: 7,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.organizationName || "-",
        },
        {
          _id: 8,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.invoiceId || "-",
        },
        {
          _id: 9,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.date ? dayjs(item?.date).format("DD MMM, YYYY") : "-",
        },
        {
          _id: 10,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.paymentMethod || "-",
        },
        // {
        //   _id: 11,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: item?.paymentStatus || "-",
        // },
        {
          _id: 11,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.subscription?.status === 1}
                onChange={() => {
                  setDeleteModal((pre) => ({
                    ...pre,
                    data: item,
                    isOpen: true,
                  }));
                }}
                disabled={
                  item?.subscription?.status === 4 || !item?.subscription
                }
              />
              <Badge
                type={item?.subscription?.status === 1 ? "success" : "danger"}
                label={item?.subscription?.status === 1 ? "Active" : "Cancel"}
              />
            </div>
          ),
        },
        {
          _id: 12,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.isEmail ? (item?.isEmail === true ? "yes" : "no") : "-",
        },
        {
          _id: 13,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 2,
              name: "Download Receipt",
              icon: "fa-regular fa-download",
              onClick: () => {
                handleDownloadReceipt(item);
                // navigate(`/admin/subscription/pdf`, {
                //   state: {
                //     data: item,
                //     type: "DonationReciptPdf",
                //     fileName: `Receipt-${item?.invoiceId || "N/A"}.pdf`,
                //   },
                // });
              },
            },
          ],
        },
      ],
    };
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          Manage Recurring Donation 
        </div>
        <div className="space-y-2">
          <div className="bg-chestnut-rose/10 rounded-md py-2 px-4">
            Explore tools and tips for funding your education, applying for the
            V’Peters Scholarship, and planning your future—all in the Resource
            Library.
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-36">
                <LimitSelect
                  value={filters?.limit}
                  allValue={data?.metadata?.totalDocs}
                  onChange={(val) =>
                    handleFilterChange("limit", val, setFilters)
                  }
                />
              </div>
              <div className="w-72">
                <Search
                  placeholder={"Search here"}
                  search={(val) => {
                    setKeyword(val);
                    handleFilterChange("page", 1, setFilters);
                  }}
                />
              </div>
            </div>
            <div className="ml-auto space-x-3">
              <span className="text-chestnut-rose font-CanelaDeck">
                Total : {data?.metadata?.totalDocs || 0}
              </span>
              <span className="text-chestnut-rose font-CanelaDeck">
                Canceled : {data?.metadata?.extraCount?.cancelSubscription || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
      {status === "ok" &&
        (permission?.list ? (
          <Table
            tableHeadData={tableHeadData}
            tableData={tableData}
            isLoder={isLoading}
            pageCount={data?.metadata?.totalPages}
            currentPage={filters?.page}
            onPageChange={(val) => {
              handleFilterChange("page", val, setFilters);
              setSelectedItems([]);
            }}
            handleOrdering={(val) => {
              console.log(val);
            }}
            isFetching={isFetching}
            isOrderingDisabled={true}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            tableActionDropdownContainer={"!w-48"}
          />
        ) : (
          <div className="font-semibold text-xl text-center h-60 flex items-center justify-center text-shades-07">
            You don't have permission to view this list.
          </div>
        ))}
      <SubscriptionModal
        open={deleteModal?.isOpen}
        loading={statusChangeInPending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        title="Cancel Recurring Payment"
        customMessage="Confirm cancellation of recurring payment?"
        deleteHandler={() => {
          statusChangeHandler({
            id: deleteModal?.data?.subscription?._id,
            status: deleteModal?.data?.subscription?.status,
          });
        }}
      />
    </div>
  );
};

export default Subscription;
